import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Children as ChildrenPropTypes } from '@instructure/ui-prop-types';
import { safeCloneElement, matchComponentTypes, omitProps, pickProps } from '@instructure/ui-react-utils';
import { capitalizeFirstLetter } from '@instructure/ui-utils';
import { themeable } from '@instructure/ui-themeable';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { GridCol } from '../GridCol';
var styles = {
  componentId: 'fxIji',
  template: function template(theme) {
    return "\n\n.fxIji_bGBk{box-sizing:border-box;display:block}\n\n.fxIji_dTOw{display:flex;flex-flow:row nowrap}\n\n.fxIji_dTOw.fxIji_lvrA{justify-content:center}\n\n.fxIji_dTOw.fxIji_bWOh{justify-content:flex-start}\n\n.fxIji_dTOw.fxIji_fNQE{justify-content:flex-end}\n\n.fxIji_dTOw.fxIji_dWwe{justify-content:space-around}\n\n.fxIji_dTOw.fxIji_bPaV{justify-content:space-between}\n\n.fxIji_dTOw.fxIji_oUBk{align-items:flex-start}\n\n.fxIji_dTOw.fxIji_NmrE{align-items:center}\n\n.fxIji_dTOw.fxIji_bwwb{align-items:flex-end}\n\n.fxIji_dTOw.fxIji_bBOa{margin:0 calc(-1*".concat(theme.spacingSmall || 'inherit', "/2)}\n\n.fxIji_dTOw.fxIji_yZGt{margin:0 calc(-1*").concat(theme.spacingMedium || 'inherit', "/2)}\n\n.fxIji_dTOw.fxIji_PsGC{margin:0 calc(-1*").concat(theme.spacingLarge || 'inherit', "/2)}\n\n.fxIji_dTOw.fxIji_buDT{margin-bottom:").concat(theme.spacingSmall || 'inherit', "}\n\n.fxIji_dTOw.fxIji_dlWR{margin-bottom:").concat(theme.spacingMedium || 'inherit', "}\n\n.fxIji_dTOw.fxIji_cGJD{margin-bottom:").concat(theme.spacingLarge || 'inherit', "}\n\n.fxIji_dTOw.fxIji_DpxJ,.fxIji_dTOw.fxIji_cKZZ{margin-bottom:0}\n\n@media screen and (--mediumMin){.fxIji_crIs{display:flex;flex-flow:row nowrap}.fxIji_crIs.fxIji_lvrA{justify-content:center}.fxIji_crIs.fxIji_bWOh{justify-content:flex-start}.fxIji_crIs.fxIji_fNQE{justify-content:flex-end}.fxIji_crIs.fxIji_dWwe{justify-content:space-around}.fxIji_crIs.fxIji_bPaV{justify-content:space-between}.fxIji_crIs.fxIji_oUBk{align-items:flex-start}.fxIji_crIs.fxIji_NmrE{align-items:center}.fxIji_crIs.fxIji_bwwb{align-items:flex-end}.fxIji_crIs.fxIji_bBOa{margin:0 calc(-1*").concat(theme.spacingSmall || 'inherit', "/2)}.fxIji_crIs.fxIji_yZGt{margin:0 calc(-1*").concat(theme.spacingMedium || 'inherit', "/2)}.fxIji_crIs.fxIji_PsGC{margin:0 calc(-1*").concat(theme.spacingLarge || 'inherit', "/2)}.fxIji_crIs.fxIji_buDT{margin-bottom:").concat(theme.spacingSmall || 'inherit', "}.fxIji_crIs.fxIji_dlWR{margin-bottom:").concat(theme.spacingMedium || 'inherit', "}.fxIji_crIs.fxIji_cGJD{margin-bottom:").concat(theme.spacingLarge || 'inherit', "}.fxIji_crIs.fxIji_DpxJ,.fxIji_crIs.fxIji_cKZZ{margin-bottom:0}}\n\n@media screen and (--largeMin){.fxIji_cpbQ{display:flex;flex-flow:row nowrap}.fxIji_cpbQ.fxIji_lvrA{justify-content:center}.fxIji_cpbQ.fxIji_bWOh{justify-content:flex-start}.fxIji_cpbQ.fxIji_fNQE{justify-content:flex-end}.fxIji_cpbQ.fxIji_dWwe{justify-content:space-around}.fxIji_cpbQ.fxIji_bPaV{justify-content:space-between}.fxIji_cpbQ.fxIji_oUBk{align-items:flex-start}.fxIji_cpbQ.fxIji_NmrE{align-items:center}.fxIji_cpbQ.fxIji_bwwb{align-items:flex-end}.fxIji_cpbQ.fxIji_bBOa{margin:0 calc(-1*").concat(theme.spacingSmall || 'inherit', "/2)}.fxIji_cpbQ.fxIji_yZGt{margin:0 calc(-1*").concat(theme.spacingMedium || 'inherit', "/2)}.fxIji_cpbQ.fxIji_PsGC{margin:0 calc(-1*").concat(theme.spacingLarge || 'inherit', "/2)}.fxIji_cpbQ.fxIji_buDT{margin-bottom:").concat(theme.spacingSmall || 'inherit', "}.fxIji_cpbQ.fxIji_dlWR{margin-bottom:").concat(theme.spacingMedium || 'inherit', "}.fxIji_cpbQ.fxIji_cGJD{margin-bottom:").concat(theme.spacingLarge || 'inherit', "}.fxIji_cpbQ.fxIji_DpxJ,.fxIji_cpbQ.fxIji_cKZZ{margin-bottom:0}}\n\n@media screen and (--xLargeMin){.fxIji_cpbQ{display:flex;flex-flow:row nowrap}.fxIji_cpbQ.fxIji_lvrA{justify-content:center}.fxIji_cpbQ.fxIji_bWOh{justify-content:flex-start}.fxIji_cpbQ.fxIji_fNQE{justify-content:flex-end}.fxIji_cpbQ.fxIji_dWwe{justify-content:space-around}.fxIji_cpbQ.fxIji_bPaV{justify-content:space-between}.fxIji_cpbQ.fxIji_oUBk{align-items:flex-start}.fxIji_cpbQ.fxIji_NmrE{align-items:center}.fxIji_cpbQ.fxIji_bwwb{align-items:flex-end}.fxIji_cpbQ.fxIji_bBOa{margin:0 calc(-1*").concat(theme.spacingSmall || 'inherit', "/2)}.fxIji_cpbQ.fxIji_yZGt{margin:0 calc(-1*").concat(theme.spacingMedium || 'inherit', "/2)}.fxIji_cpbQ.fxIji_PsGC{margin:0 calc(-1*").concat(theme.spacingLarge || 'inherit', "/2)}.fxIji_cpbQ.fxIji_buDT{margin-bottom:").concat(theme.spacingSmall || 'inherit', "}.fxIji_cpbQ.fxIji_dlWR{margin-bottom:").concat(theme.spacingMedium || 'inherit', "}.fxIji_cpbQ.fxIji_cGJD{margin-bottom:").concat(theme.spacingLarge || 'inherit', "}.fxIji_cpbQ.fxIji_DpxJ,.fxIji_cpbQ.fxIji_cKZZ{margin-bottom:0}}\n\n.fxIji_fFVr{outline:0.0625rem dashed #00f}");
  },
  'root': 'fxIji_bGBk',
  'startAtSmall': 'fxIji_dTOw',
  'hAlign--center': 'fxIji_lvrA',
  'hAlign--start': 'fxIji_bWOh',
  'hAlign--end': 'fxIji_fNQE',
  'hAlign--space-around': 'fxIji_dWwe',
  'hAlign--space-between': 'fxIji_bPaV',
  'vAlign--top': 'fxIji_oUBk',
  'vAlign--middle': 'fxIji_NmrE',
  'vAlign--bottom': 'fxIji_bwwb',
  'colSpacingSmall': 'fxIji_bBOa',
  'colSpacingMedium': 'fxIji_yZGt',
  'colSpacingLarge': 'fxIji_PsGC',
  'rowSpacingSmall': 'fxIji_buDT',
  'rowSpacingMedium': 'fxIji_dlWR',
  'rowSpacingLarge': 'fxIji_cGJD',
  'lastRow': 'fxIji_DpxJ',
  'rowSpacingNone': 'fxIji_cKZZ',
  'startAtMedium': 'fxIji_crIs',
  'startAtLarge': 'fxIji_cpbQ',
  'visualDebug': 'fxIji_fFVr'
};
import theme from './theme';
/**
---
parent: DeprecatedGrid
id: DeprecatedGrid.Row
---
**/

var GridRow = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(GridRow, _Component);

  var _super = _createSuper(GridRow);

  function GridRow() {
    _classCallCheck(this, GridRow);

    return _super.apply(this, arguments);
  }

  _createClass(GridRow, [{
    key: "startAtClass",

    /* eslint-disable react/require-default-props */

    /* eslint-enable react/require-default-props */
    value: function startAtClass() {
      return !!this.props.startAt && "startAt".concat(capitalizeFirstLetter(this.props.startAt));
    }
  }, {
    key: "rowSpacingClass",
    value: function rowSpacingClass() {
      return "rowSpacing".concat(capitalizeFirstLetter(this.props.rowSpacing));
    }
  }, {
    key: "colSpacingClass",
    value: function colSpacingClass() {
      return "colSpacing".concat(capitalizeFirstLetter(this.props.colSpacing));
    }
  }, {
    key: "renderChildren",
    value: function renderChildren() {
      var _this = this;

      var _this$props = this.props,
          children = _this$props.children,
          props = _objectWithoutProperties(_this$props, ["children"]);

      return Children.map(children, function (child, index) {
        if (matchComponentTypes(child, [GridCol])) {
          return safeCloneElement(child, _objectSpread({}, pickProps(_this.props, GridRow.propTypes), {}, child.props, {
            /* child props should override parent */
            isLastRow: props.isLastRow,
            isLastCol: index + 1 === Children.count(children)
          }));
        } else {
          return child; // PropType validation should handle errors
        }
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _classes;

      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles.lastRow, this.props.isLastRow), _defineProperty(_classes, styles["hAlign--".concat(this.props.hAlign)], true), _defineProperty(_classes, styles["vAlign--".concat(this.props.vAlign)], true), _defineProperty(_classes, styles[this.rowSpacingClass()], true), _defineProperty(_classes, styles[this.colSpacingClass()], this.props.colSpacing !== 'none'), _defineProperty(_classes, styles[this.startAtClass()], !!this.props.startAt), _classes);
      var props = omitProps(this.props, GridRow.propTypes);
      return /*#__PURE__*/React.createElement("span", Object.assign({}, props, {
        className: classnames(classes)
      }), this.renderChildren());
    }
  }]);

  GridRow.displayName = "GridRow";
  return GridRow;
}(Component), _class2.propTypes = {
  children: ChildrenPropTypes.oneOf([GridCol, ScreenReaderContent]),
  rowSpacing: PropTypes.oneOf(['none', 'small', 'medium', 'large']),
  colSpacing: PropTypes.oneOf(['none', 'small', 'medium', 'large']),
  hAlign: PropTypes.oneOf(['start', 'center', 'end', 'space-around', 'space-between']),
  vAlign: PropTypes.oneOf(['top', 'middle', 'bottom']),
  startAt: PropTypes.oneOf(['small', 'medium', 'large', 'x-large', null]),
  visualDebug: PropTypes.bool,
  isLastRow: PropTypes.bool
}, _class2.defaultProps = {
  children: null,
  isLastRow: false
}, _temp)) || _class);
export default GridRow;
export { GridRow };